import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Menu, BarChart3, FileText, Plus, Upload, Sparkles, BookOpen, LogOut, User, Users, Settings, TrendingUp, Shield, History, Grid3x3, Folder, FileBarChart, Bell, Moon, Sun, Star, Building2, Scale, GitBranch, Target, ClipboardList } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import axios from 'axios';
import Buzon from './Buzon';
import './Header.css';
import './HeaderExtra.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Header = ({ currentView, onViewChange }) => {
  const { user, logout, isAdmin } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const [showBuzon, setShowBuzon] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const buzonRef = useRef(null);

  // Función para calcular el conteo de notificaciones
  const fetchNotificationCount = useCallback(async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/gestiones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const gestiones = response.data || [];
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const enSieteDias = new Date();
      enSieteDias.setDate(enSieteDias.getDate() + 7);

      // Contar gestiones asignadas al usuario
      const asignadas = gestiones.filter(g => 
        (g.nombre_asesor_cnc === user?.full_name || 
         g.nombre_asesor_cnc === user?.email) &&
        g.estado !== 'Completada / Archivada'
      ).length;

      // Contar tareas por vencer CNC
      const vencerCNC = gestiones.filter(g => {
        if (g.estado === 'Completada / Archivada') return false;
        if (!g.fecha_vencimiento) return false;
        const fechaVenc = new Date(g.fecha_vencimiento);
        return fechaVenc <= enSieteDias && fechaVenc >= hoy;
      }).length;

      // Contar tareas por vencer Probidad
      const vencerProbidad = gestiones.filter(g => {
        if (g.estado === 'Completada / Archivada') return false;
        if (!g.fecha_vencimiento_probidad) return false;
        const fechaVenc = new Date(g.fecha_vencimiento_probidad);
        return fechaVenc <= enSieteDias && fechaVenc >= hoy;
      }).length;
      
      // Contar tareas VENCIDAS de Probidad
      const vencidasProbidad = gestiones.filter(g => {
        if (g.estado === 'Completada / Archivada') return false;
        if (!g.fecha_vencimiento_probidad) return false;
        const fechaVenc = new Date(g.fecha_vencimiento_probidad);
        fechaVenc.setHours(23, 59, 59, 999);
        return fechaVenc < hoy;
      }).length;

      setNotificationCount(asignadas + vencerCNC + vencerProbidad + vencidasProbidad);
    } catch (error) {
      console.error('Error al cargar conteo de notificaciones:', error);
    }
  }, [user]);

  // Cargar conteo inicial y actualizar cada 30 segundos
  useEffect(() => {
    fetchNotificationCount();
    const interval = setInterval(fetchNotificationCount, 30000);
    return () => clearInterval(interval);
  }, [fetchNotificationCount]);

  // Cerrar buzón al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (buzonRef.current && !buzonRef.current.contains(event.target)) {
        setShowBuzon(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBuzonGestionSelect = (gestionId) => {
    onViewChange('gestiones');
    // Emitir evento personalizado para que VistaGeneralGestiones lo capture
    window.dispatchEvent(new CustomEvent('selectGestion', { detail: { gestionId } }));
    setShowBuzon(false);
  };

  return (
    <header className="header" data-testid="header">
      <div className="container header-content">
        <div className="brand" data-testid="brand">
          <img 
            src="/logo-alerta360.png" 
            alt="ALERTA 360" 
            className="brand-logo-img"
            style={{ height: '45px', objectFit: 'contain' }}
          />
        </div>

        <nav className="nav" data-testid="nav">
          <button
            data-testid="nav-home"
            className={`nav-btn ${currentView === 'home' ? 'active' : ''}`}
            onClick={() => onViewChange('home')}
          >
            <Menu size={18} />
            Inicio
          </button>
          
          <button
            data-testid="nav-modulos"
            className={`nav-btn ${currentView === 'modulos' ? 'active' : ''}`}
            onClick={() => onViewChange('modulos')}
          >
            <Grid3x3 size={18} />
            Módulos
          </button>
          
          <button
            data-testid="nav-gestiones"
            className={`nav-btn ${currentView === 'gestiones' ? 'active' : ''}`}
            onClick={() => onViewChange('gestiones')}
          >
            <Folder size={18} />
            Gestiones
          </button>
          
          <button
            data-testid="nav-dashboard"
            className={`nav-btn ${currentView === 'dashboard' ? 'active' : ''}`}
            onClick={() => onViewChange('dashboard')}
          >
            <BarChart3 size={18} />
            Tablero
          </button>
          
          <button
            data-testid="nav-rendimiento"
            className={`nav-btn ${currentView === 'tablero-rendimiento' ? 'active' : ''}`}
            onClick={() => onViewChange('tablero-rendimiento')}
          >
            <Users size={18} />
            Rendimiento
          </button>
          
          <button
            data-testid="nav-calificaciones"
            className={`nav-btn ${currentView === 'consulta-calificaciones' ? 'active' : ''}`}
            onClick={() => onViewChange('consulta-calificaciones')}
          >
            <Star size={18} />
            Calificaciones
          </button>
          
          <button
            data-testid="nav-asignaciones"
            className={`nav-btn ${currentView === 'consulta-asignaciones' ? 'active' : ''}`}
            onClick={() => onViewChange('consulta-asignaciones')}
          >
            <Building2 size={18} />
            Asignaciones
          </button>
          
          <button
            data-testid="nav-probidad"
            className={`nav-btn ${currentView === 'consulta-probidad' ? 'active' : ''}`}
            onClick={() => onViewChange('consulta-probidad')}
          >
            <Scale size={18} />
            Probidad
          </button>
          
          <button
            data-testid="nav-seguimiento"
            className={`nav-btn ${currentView === 'seguimiento-instituciones' ? 'active' : ''}`}
            onClick={() => onViewChange('seguimiento-instituciones')}
          >
            <GitBranch size={18} />
            Seguimiento
          </button>
          
          <button
            data-testid="nav-tendencia"
            className={`nav-btn ${currentView === 'tendencia-monitoreo' ? 'active' : ''}`}
            onClick={() => onViewChange('tendencia-monitoreo')}
          >
            <TrendingUp size={18} />
            Tendencia
          </button>

          <button
            data-testid="nav-control-asignaciones"
            className={`nav-btn ${currentView === 'control-asignaciones' ? 'active' : ''}`}
            onClick={() => onViewChange('control-asignaciones')}
          >
            <ClipboardList size={18} />
            Control CNC
          </button>
          
          {isAdmin() && (
            <>
              <button
                data-testid="nav-panel-carga"
                className={`nav-btn ${currentView === 'panel-carga' ? 'active' : ''}`}
                onClick={() => onViewChange('panel-carga')}
              >
                <Target size={18} />
                Carga Trabajo
              </button>
              <button
                data-testid="nav-admin"
                className={`nav-btn ${currentView === 'admin' ? 'active' : ''}`}
                onClick={() => onViewChange('admin')}
              >
                <Shield size={18} />
                Administración
              </button>
            </>
          )}
          
          <div className="header-divider"></div>
          
          {/* Buzón de Notificaciones */}
          <div className="buzon-container" ref={buzonRef}>
            <button
              className={`nav-btn nav-btn-buzon ${showBuzon ? 'active' : ''}`}
              onClick={() => {
                setShowBuzon(!showBuzon);
                if (!showBuzon) fetchNotificationCount(); // Actualizar al abrir
              }}
              data-testid="buzon-btn"
              title="Buzón de Notificaciones"
              style={{ position: 'relative' }}
            >
              <Bell size={18} />
              {notificationCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  background: '#EF4444',
                  color: 'white',
                  fontSize: '10px',
                  fontWeight: 700,
                  borderRadius: '50%',
                  minWidth: '18px',
                  height: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px solid white'
                }}>
                  {notificationCount > 99 ? '99+' : notificationCount}
                </span>
              )}
            </button>
            {showBuzon && (
              <Buzon 
                onSelectGestion={handleBuzonGestionSelect}
                onClose={() => setShowBuzon(false)}
              />
            )}
          </div>
          
          {/* Theme Toggle */}
          <button
            className="theme-toggle"
            onClick={toggleTheme}
            title={isDarkMode ? 'Cambiar a modo claro' : 'Cambiar a modo nocturno'}
            data-testid="theme-toggle-btn"
          >
            {isDarkMode ? <Sun size={16} /> : <Moon size={16} />}
            <span style={{ fontSize: '0.8rem' }}>{isDarkMode ? 'Claro' : 'Oscuro'}</span>
          </button>
          
          <div className="user-menu">
            <span className="user-info">
              <User size={16} />
              {user?.full_name}
            </span>
            <button
              className="nav-btn nav-btn-logout"
              onClick={logout}
              data-testid="logout-btn"
            >
              <LogOut size={18} />
              Salir
            </button>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
